import * as S from './ServiceTemplates.styled'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import { Button, ServiceTemplateDeleteModal, TinyMCE } from '@/components'
import { GetGlobalServiceInformationTemplatesNormalized } from '@messenger/types'
import { memo, useMemo, useState } from 'react'
import {
  deleteServiceInformationTemplate,
  getServiceInformationTemplates,
} from '@/api/serviceInformationTemplates'
import { useSetToast } from '@/hooks'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import {
  caseFuneralHomeAtom,
  caseSelector,
  dateFormatAtom,
  selectedThemeAtom,
  themeAtom,
  themeOverrideAtom,
} from '@/atoms'
import {
  pageSetLocaleAtom,
  serviceInformationTemplatesAtom,
} from '@/atoms/_editorAtoms'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { mergedBoxHtml } from '@/utils/tinyMCE/updateDynamicText/mergedBoxHtml'
import { useTranslation } from 'react-i18next'
import Icon from '@/components/priority/Icon/Icon'
import i18n from '@/i18n'
import formatDate from '@/utils/dates/formatDate'
import {
  parseEditorServiceTemplateContent,
  resolveEditorField,
  toEditorTextBoxCaseData,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { EditorFieldParams } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'

type ServiceTemplateItemProps = {
  template: GetGlobalServiceInformationTemplatesNormalized
  allowActions: boolean
  /** A Save is in flight: the card is shown, but it does not select. */
  disabled?: boolean
  onTemplateSelect: (
    template: GetGlobalServiceInformationTemplatesNormalized,
  ) => void
  onDeleteTemplate?: (
    template: GetGlobalServiceInformationTemplatesNormalized,
  ) => void
  onEditTemplate?: (
    template: GetGlobalServiceInformationTemplatesNormalized,
  ) => void
}

const ServiceTemplateItem = ({
  template,
  allowActions = true,
  disabled = false,
  onTemplateSelect,
  onEditTemplate,
}: ServiceTemplateItemProps) => {
  countEditorProbeRender('ServiceTemplateItem')
  useEditorProbeLifetime('ServiceTemplateItem')
  const { t } = useTranslation()
  const setToast = useSetToast()
  const handleError = useErrorHandler('ServiceTemplateItem')

  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false)

  const theme = useRecoilValue(themeAtom)
  const isDarkTheme = theme === 'dark'
  const [invertedBg, setInvertedBg] = useState(false)
  const isDarkBg = isDarkTheme ? !invertedBg : invertedBg

  const themeOverride = useRecoilValue(themeOverrideAtom) as {
    product_id: string
  }
  const caseDetails = useRecoilValue(caseSelector)
  const funeralHomeDetails = useRecoilValue(caseFuneralHomeAtom)
  const dateFormat = useRecoilValue(dateFormatAtom)
  const pageSetLocale = useRecoilValue(pageSetLocaleAtom)
  const selectedTheme = useRecoilValue(selectedThemeAtom)

  // Setter only — subscribing to the templates atom value here forced every
  // card (and its TinyMCE preview) to re-render whenever any template was
  // created/updated/deleted, even though the preview content never depends
  // on the other templates.
  const setServiceInformationTemplates = useSetRecoilState(
    serviceInformationTemplatesAtom,
  )

  const handleDeleteTemplate = async (
    template: GetGlobalServiceInformationTemplatesNormalized,
  ) => {
    try {
      await deleteServiceInformationTemplate(template.id)

      const productId = themeOverride?.product_id

      if (!productId) {
        handleError(
          t('editor.serviceTemplates.notifications.productIdNotFound'),
          new Error('Product ID not found'),
        )
        return
      }

      const serviceInformationTemplatesData =
        await getServiceInformationTemplates(productId)

      setServiceInformationTemplates(serviceInformationTemplatesData.data)

      setToast({
        text: t('editor.serviceTemplates.templateRemovedSuccessfully'),
        icon: 'success',
      })

      setShowDeleteTemplateModal(false)
    } catch (error) {
      const err = error as { message?: string }
      handleError(
        err?.message || t('editor.serviceTemplates.failedToDeleteTemplate'),
        error as Error,
      )
    }
  }

  const editorTemplateContent = useMemo(
    () => parseEditorServiceTemplateContent(template.template),
    [template.template],
  )

  const editorTemplatePreviewText = useMemo(() => {
    if (editorTemplateContent === null) return ''
    const caseData = toEditorTextBoxCaseData(
      caseDetails as Record<string, unknown> | null,
      {
        dateFormat,
        locale: pageSetLocale,
        formatDate,
        funeralHomeDetails: funeralHomeDetails as Record<
          string,
          unknown
        > | null,
        themeApplication: (selectedTheme as { application?: string } | null)
          ?.application,
        translate: (key, values) =>
          i18n.t(key, { ...values, lng: pageSetLocale }),
      },
    )
    return editorTemplateContent.blocks
      .map((block) =>
        block.runs
          .map((run) =>
            run.kind === 'text'
              ? run.characters
              : resolveEditorField(
                  run.fieldKey,
                  caseData,
                  run.params as EditorFieldParams | undefined,
                ),
          )
          .join(''),
      )
      .filter((line) => line.trim() !== '')
      .join('\n')
  }, [
    editorTemplateContent,
    caseDetails,
    dateFormat,
    pageSetLocale,
    funeralHomeDetails,
    selectedTheme,
  ])

  // Memoize template content processing to avoid expensive recalculation on every render
  const processedTemplateContent = useMemo(() => {
    const rawContent = template.template
    if (!rawContent || editorTemplateContent !== null) return ''

    return mergedBoxHtml(rawContent, {
      caseDetails,
      funeralHomeDetails,
      dateFormat,
      pageSetLocale,
      environment: 'editor',
      selectedTheme,
    })
  }, [
    template.template,
    editorTemplateContent,
    caseDetails,
    funeralHomeDetails,
    dateFormat,
    pageSetLocale,
    selectedTheme,
  ])

  return (
    <Flex
      column
      borderBottom="1px solid"
      borderColor="gray4"
      padding="24px 0 12px 0"
    >
      <S.TemplateItem
        column
        $disabled={disabled}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : () => onTemplateSelect(template)}
        data-testid="service-template-card"
      >
        <Flex align="center" justify="space-between" margin="0 0 12px 0">
          <Flex column>
            <Text weight="600" size="14px" color="black">
              {template.name}
            </Text>
            <Text size="12px" color="gray2">
              {t(
                `editor.serviceTemplates.categories.${
                  template.category ?? 'DEFAULT'
                }`,
              )}
            </Text>
          </Flex>
          <S.BackgroundToggle
            $active={isDarkBg}
            onClick={(e) => {
              e.stopPropagation()
              setInvertedBg((prev) => !prev)
            }}
            title={t('editor.serviceTemplates.invertBackground')}
          >
            <Icon icon={isDarkBg ? 'moon' : 'sun'} />
            {isDarkBg
              ? t('editor.serviceTemplates.darkBackground')
              : t('editor.serviceTemplates.lightBackground')}
          </S.BackgroundToggle>
        </Flex>
        <S.TemplatePreview $darkBg={isDarkBg}>
          {editorTemplateContent !== null ? (
            <div
              data-editor-service-template-preview={template.id}
              style={{ whiteSpace: 'pre-wrap', padding: '12px' }}
            >
              {editorTemplatePreviewText}
            </div>
          ) : (
            <TinyMCE
              probeLabel="preview"
              init={{ toolbar: [] }}
              value={processedTemplateContent}
              disabled
            />
          )}
        </S.TemplatePreview>
      </S.TemplateItem>
      {allowActions && (
        <Flex gap="8px" justify="flex-start" margin="0 0 12px 0">
          <Button onClick={() => setShowDeleteTemplateModal(true)} destructive>
            {t('editor.serviceTemplates.deleteButton')}
          </Button>
          {editorTemplateContent === null && (
            <Button primary onClick={() => onEditTemplate?.(template)}>
              {t('editor.serviceTemplates.editButton')}
            </Button>
          )}
        </Flex>
      )}
      <ServiceTemplateDeleteModal
        template={template}
        show={showDeleteTemplateModal}
        setShow={setShowDeleteTemplateModal}
        onDeleteTemplate={handleDeleteTemplate}
      />
    </Flex>
  )
}

export default memo(ServiceTemplateItem)
